import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";

function Delivery() {
  const dispatchTime = siteConfig.policies?.dispatchTime || "1–2 business days";
  const lastUpdated = siteConfig.policies?.lastUpdated || "August 14, 2026";

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="delivery policy-page">
      <header className="policy-page__hero">
        <p className="policy-page__kicker">Customer care</p>
        <h1>Shipping &amp; delivery</h1>
        <p>
          From our shelves to your ritual—clear delivery choices, considered
          packing and tracking at every step.
        </p>
        <span>Last updated {lastUpdated}</span>
      </header>

      <section className="policy-summary" aria-label="Shipping at a glance">
        <article>
          <strong>{dispatchTime}</strong>
          <span>typical order processing time</span>
        </article>
        <article>
          <strong>{siteConfig.currencySymbol}{siteConfig.shippingFlatRate}</strong>
          <span>fixed tracked shipping per order</span>
        </article>
        <article>
          <strong>Tracked delivery</strong>
          <span>updates sent to your checkout email</span>
        </article>
      </section>

      <div className="policy-page__layout">
        <nav className="policy-page__nav" aria-label="Shipping policy sections">
          <span>On this page</span>
          <a href="#shipping-processing">Processing</a>
          <a href="#shipping-options">Delivery options</a>
          <a href="#shipping-destinations">Destinations</a>
          <a href="#shipping-tracking">Tracking &amp; issues</a>
          <a href="#shipping-contact">Contact</a>
        </nav>

        <article className="policy-page__content">
          <section className="policy-page__lead" aria-label="Shipping overview">
            <p>
              Delivery services, estimates and final charges available for your
              address appear at checkout. Timelines below begin after dispatch
              and exclude weekends, public holidays and carrier disruptions.
            </p>
          </section>

          <section id="shipping-processing">
            <p className="policy-page__number">01</p>
            <h2>Order processing</h2>
            <p>
              Orders are usually prepared within {dispatchTime}. Orders placed
              after the daily cut-off, on weekends or public holidays begin
              processing the next business day. During launches and seasonal
              periods, preparation may take longer. We will email you if an item
              becomes unavailable or a material delay affects your order.
            </p>
            <p>
              Changes or cancellations are not guaranteed after checkout because
              fulfillment begins quickly. Contact us as soon as possible and we
              will help where we can.
            </p>
          </section>

          <section id="shipping-options">
            <p className="policy-page__number">02</p>
            <h2>Delivery option</h2>
            <div className="shipping-table" role="table" aria-label="Estimated shipping times">
              <div role="row" className="shipping-table__head">
                <span role="columnheader">Service</span>
                <span role="columnheader">Estimated transit</span>
                <span role="columnheader">Cost</span>
              </div>
              <div role="row">
                <span role="cell">Tracked</span>
                <span role="cell">Shown for your destination at checkout</span>
                <span role="cell">{siteConfig.currencySymbol}{siteConfig.shippingFlatRate}</span>
              </div>
            </div>
            <p>
              The fixed shipping charge is added once per order and is not changed
              by a discount code. We cannot ship to freight-forwarding addresses or locations a
              carrier marks as unsafe or inaccessible.
            </p>
          </section>

          <section id="shipping-destinations">
            <p className="policy-page__number">03</p>
            <h2>Available destinations</h2>
            <p>
              The United Kingdom is the default country at checkout. The other
              countries and regions currently supported are shown in the checkout
              selector. Destinations subject to applicable trade or payment
              restrictions are not available. Delivery timing and any taxes
              required for the selected address are shown before payment; carrier
              review can add time outside our control.
            </p>
          </section>

          <section id="shipping-tracking">
            <p className="policy-page__number">04</p>
            <h2>Tracking, delays and missing parcels</h2>
            <p>
              A dispatch email with tracking is sent when the carrier receives
              your parcel. Tracking can take up to 48 hours to update. If a parcel
              is marked delivered but cannot be located, check safe places,
              household members and neighbors, then allow one business day before
              contacting us. Report missing or damaged deliveries within 7 days
              so we can investigate with the carrier.
            </p>
            <p>
              We are not responsible for delays or loss caused by an incomplete
              address supplied at checkout. If an undeliverable parcel returns to
              us, we can refund eligible merchandise less original and return
              delivery costs, or arrange reshipment at the customer&apos;s expense.
            </p>
          </section>

          <section>
            <p className="policy-page__number">05</p>
            <h2>Considered packaging</h2>
            <p>
              Orders are packed to protect glass and preserve product quality
              while avoiding unnecessary material. Where possible, we use
              recyclable paper-based packaging. Please follow local recycling
              guidance and keep products away from extreme heat or cold after
              delivery.
            </p>
          </section>

          <section id="shipping-contact">
            <p className="policy-page__number">06</p>
            <h2>Questions about your delivery?</h2>
            <p>
              Contact us at <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>{" "}
              with your order number and delivery postcode. Our team is
              available {siteConfig.contact.hours}. For sending an item back,
              please read our <Link to="/returns">returns policy</Link> first.
            </p>
          </section>

          <div className="policy-page__related">
            <span>Related policies</span>
            <Link to="/returns" onClick={toTop}>Returns</Link>
            <Link to="/privacy" onClick={toTop}>Privacy</Link>
            <Link to="/terms" onClick={toTop}>Terms &amp; conditions</Link>
          </div>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
